// assets/km-shop.js
// Keksmeister storefront components: free-shipping progress, delivery estimate,
// "4 für 3" bundle box, "8 für 6" set builder, card add buttons, the Teigleisten add-on, seasonal banners,
// product row scrolling, "Zuletzt angesehen", the newsletter popup and the B2B tier calculator. Cart updates are announced with
// Shopify's standard CartLinesUpdateEvent so Horizon's cart drawer, bubble and items refresh.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney as formatShopMoney } from '@theme/money-formatting';

const moneyFormatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

/**
 * Formats like Liquid's `money` filter when the element (or an ancestor) carries the shop's
 * data-money-format, so live updates match the server-rendered prices; otherwise "7,90 €".
 * @param {number} cents
 * @param {Element} [context]
 */
function formatMoney(cents, context) {
  const format = context?.closest('[data-money-format]')?.getAttribute('data-money-format');
  if (format) {
    try {
      return formatShopMoney(cents, format, window.Shopify?.currency?.active || 'EUR');
    } catch {
      /* fall back below */
    }
  }
  return moneyFormatter.format(cents / 100);
}

function cartAddUrl() {
  // Theme.routes.cart_add_url already ends in .js (snippets/scripts.liquid)
  return window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
}

function cartUrl() {
  return `${window.Theme?.routes?.cart_url ?? '/cart'}.js`;
}

/** Section IDs of rendered cart item lists, so the add response can carry fresh markup. */
function cartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

/**
 * Adds several variants at once and announces the update like Horizon's product form does.
 * @param {HTMLElement} source
 * @param {{ id: number, quantity: number, properties?: Record<string, string> }[]} items
 */
export async function addItemsToCart(source, items) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const data = await response.json();
    const cart = await fetch(cartUrl(), { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const didError = Boolean(data.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: data.sections, didError, source: 'km-shop', itemCount: items.length },
    });
    if (didError) throw new Error(data.description || data.message || 'Add to cart failed');
    return data;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/* ------------------------------------------------------------------ */
/* Free-shipping progress                                              */
/* ------------------------------------------------------------------ */
class KmFreeShipping extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal: this.#abort.signal });
    this.render(Number(this.dataset.total || 0));
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(async () => {
        const cart = await fetch(cartUrl(), { headers: { Accept: 'application/json' } }).then((r) => r.json());
        this.render(cart.total_price);
      })
      .catch(() => {});
  };

  /** @param {number} total cents */
  render(total) {
    const threshold = Number(this.dataset.threshold || 0);
    if (!threshold) return;
    const remaining = Math.max(threshold - total, 0);
    const progress = Math.min(total / threshold, 1);
    // Product page: does this product alone reach the threshold? (data-price from blocks/km-free-shipping)
    const price = Number(this.dataset.price || 0);
    const withProduct = remaining > 0 && price > 0 && price >= remaining;
    const bar = this.querySelector('[data-km-progress]');
    const label = this.querySelector('[data-km-label]');
    if (bar instanceof HTMLElement) bar.style.setProperty('--km-progress', String(progress));
    if (label) {
      label.textContent = withProduct
        ? this.dataset.textWithProduct || 'Mit diesem Artikel ist der Versand kostenlos.'
        : remaining > 0
          ? (this.dataset.textRemaining || 'Noch [betrag] bis zum Gratisversand').replace('[betrag]', formatMoney(remaining, this))
          : this.dataset.textReached || 'Der Versand ist kostenlos.';
    }
    this.toggleAttribute('reached', remaining === 0);
  }
}

/* ------------------------------------------------------------------ */
/* Delivery estimate                                                   */
/* ------------------------------------------------------------------ */
class KmDelivery extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.update();
    this.#timer = window.setInterval(() => this.update(), 60_000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  get holidays() {
    return new Set((this.dataset.holidays || '').split(/[\s,]+/).filter(Boolean));
  }

  /** @param {Date} date */
  isWorkday(date) {
    const day = date.getDay();
    if (day === 0 || day === 6) return false;
    return !this.holidays.has(this.#iso(date));
  }

  /** @param {Date} date */
  #iso(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  /** @param {Date} start @param {number} days */
  addWorkdays(start, days) {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      if (this.isWorkday(date)) added++;
    }
    return date;
  }

  update() {
    const now = new Date();
    const cutoff = Number(this.dataset.cutoff || 13);
    const prodMax = Number(this.dataset.productionMax || 2);
    const shipMin = Number(this.dataset.shippingMin || 1);
    const shipMax = Number(this.dataset.shippingMax || 2);

    // Production starts today if it's a workday before the cut-off, otherwise next workday.
    let start = new Date(now);
    const beforeCutoff = this.isWorkday(now) && now.getHours() < cutoff;
    if (!beforeCutoff) start = this.addWorkdays(now, 1);
    start.setHours(12, 0, 0, 0);

    const shipBy = prodMax > 1 ? this.addWorkdays(start, prodMax - 1) : start;
    const arriveFrom = this.addWorkdays(shipBy, shipMin);
    const arriveTo = this.addWorkdays(shipBy, shipMax);

    const fmt = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
    const set = (/** @type {string} */ key, /** @type {string} */ value) => {
      const el = this.querySelector(`[data-km-${key}]`);
      if (el) el.textContent = value;
    };

    if (beforeCutoff) {
      const cutoffDate = new Date(now);
      cutoffDate.setHours(cutoff, 0, 0, 0);
      const minutes = Math.max(0, Math.round((cutoffDate.getTime() - now.getTime()) / 60000));
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      set('countdown', `Bestellst du in den nächsten ${h > 0 ? `${h} Std. ` : ''}${m} Min.`);
    } else {
      set('countdown', 'Bestellst du jetzt');
    }
    set('ship', `am ${fmt.format(shipBy)}`);
    set('arrive', arriveFrom.getTime() === arriveTo.getTime() ? `am ${fmt.format(arriveTo)}` : `zwischen ${fmt.format(arriveFrom)} und ${fmt.format(arriveTo)}`);
    this.removeAttribute('pending');
  }
}

/* ------------------------------------------------------------------ */
/* "4 für 3" math, bundle box and "8 für 6" set builder                */
/* ------------------------------------------------------------------ */

/**
 * Same rule as the automatic cart discount: for every 4 qualifying items the cheapest one is free.
 * Ties go to the later item, so the 4th of four equally priced motifs shows "gratis".
 * @param {number[]} prices cents, in display order
 * @returns {{ total: number, discount: number, free: Set<number> }}
 */
export function fourForThree(prices) {
  const order = prices.map((price, index) => ({ price, index })).sort((a, b) => a.price - b.price || b.index - a.index);
  const freeCount = Math.floor(prices.length / 4);
  const free = new Set(order.slice(0, freeCount).map((entry) => entry.index));
  const total = prices.reduce((sum, price) => sum + price, 0);
  const discount = order.slice(0, freeCount).reduce((sum, entry) => sum + entry.price, 0);
  return { total, discount, free };
}

/**
 * Checkbox list of variants added in one go (<km-bundle> on product pages, <km-set-builder> for "8 für 6").
 * Items: [data-km-item] containing an input[data-variant-id][data-price]. Marks free items with [free],
 * writes [data-km-bundle-total|compare|label|count] and adds the checked variants via addItemsToCart.
 * Dog motifs: radios [data-km-tier-pick] (4 or 8) show and check the items with data-km-tier <= the choice;
 * hidden items do not count.
 */
class KmBundle extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches('[data-km-tier-pick]')) this.setTier(Number(target.value));
        this.update();
      },
      { signal }
    );
    this.querySelector('[data-km-bundle-add]')?.addEventListener('click', () => this.add(), { signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.update();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get inputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="checkbox"][data-variant-id]')]).filter(
      (input) => !(/** @type {HTMLElement | null} */ (input.closest('[data-km-item]'))?.hidden)
    );
  }

  /** Shows the items of the chosen size (4 or 8 motifs) and checks all of them; hides and unchecks the rest. */
  setTier(/** @type {number} */ tier) {
    for (const item of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-km-item][data-km-tier]'))) {
      const show = Number(item.dataset.kmTier) <= tier;
      item.hidden = !show;
      if (!show) item.removeAttribute('free');
      const input = item.querySelector('input[type="checkbox"]');
      if (input instanceof HTMLInputElement) input.checked = show;
    }
    // A new choice starts with all its motifs checked
    for (const input of this.inputs) input.checked = true;
  }

  get selected() {
    return this.inputs.filter((input) => input.checked);
  }

  /** Keeps "Dieses Motiv" in sync with the size picked on the product page. */
  #onProductSelect = (/** @type {Event & { promise?: Promise<any> }} */ event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card, km-bundle')) return;
    event.promise
      ?.then((result) => {
        const variant = result?.variant;
        const input = this.querySelector('input[data-km-current]');
        if (!variant || !(input instanceof HTMLInputElement)) return;
        const id = String(variant.id).split('/').pop();
        const price = Math.round(Number(variant.price?.amount) * 100);
        if (!id || !Number.isFinite(price)) return;
        input.dataset.variantId = id;
        input.dataset.price = String(price);
        const item = input.closest('[data-km-item]');
        const regular = item?.querySelector('.km-bundle__regular');
        if (regular) regular.textContent = formatMoney(price, this);
        const size = item?.querySelector('[data-km-variant-title]');
        if (size && variant.title) size.textContent = variant.title;
        this.update();
      })
      .catch(() => {});
  };

  update() {
    const inputs = this.inputs;
    const checked = inputs.filter((input) => input.checked);
    const { total, discount, free } = fourForThree(checked.map((input) => Number(input.dataset.price || 0)));
    const freeInputs = new Set([...free].map((index) => checked[index]));
    for (const input of inputs) input.closest('[data-km-item]')?.toggleAttribute('free', freeInputs.has(input));

    const set = (/** @type {string} */ key, /** @type {string} */ value) => {
      for (const el of this.querySelectorAll(`[data-km-bundle-${key}]`)) el.textContent = value;
    };
    set('total', formatMoney(total - discount, this));
    set('compare', discount > 0 ? formatMoney(total, this) : '');
    set('count', String(checked.length));
    const all = checked.length === inputs.length;
    const template = all ? this.dataset.labelAll : this.dataset.labelSome;
    if (template) set('label', template.replace('[n]', String(checked.length)));
    const button = this.querySelector('[data-km-bundle-add]');
    if (button instanceof HTMLButtonElement) button.disabled = checked.length === 0;
    this.toggleAttribute('discounted', discount > 0);
  }

  async add() {
    const button = this.querySelector('[data-km-bundle-add]');
    const status = this.querySelector('[data-km-bundle-status]');
    const items = this.selected.map((input) => ({ id: Number(input.dataset.variantId), quantity: 1 }));
    if (!items.length) return;
    if (button instanceof HTMLButtonElement) button.setAttribute('aria-busy', 'true');
    try {
      await addItemsToCart(this, items);
      if (status) status.textContent = this.dataset.textAdded || 'Liegt im Warenkorb.';
    } catch (error) {
      if (status) status.textContent = this.dataset.textError || 'Das hat leider nicht geklappt. Bitte versuch es noch einmal.';
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }
}

class KmSetBuilder extends KmBundle {}

/* ------------------------------------------------------------------ */
/* "In den Warenkorb" on product cards and tiles                       */
/* ------------------------------------------------------------------ */
class KmCardAdd extends HTMLElement {
  #abort = new AbortController();
  #timer = 0;

  connectedCallback() {
    // New controller on every connect: the cart drawer re-renders by morphing, which can move these elements
    // (disconnect + connect). With the old, aborted signal the listener was gone and the next tap posted the
    // form natively to /cart/add (23.10.2026). Listening on the element also survives a replaced <form>.
    this.#abort = new AbortController();
    this.addEventListener('submit', this.#onSubmit, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    window.clearTimeout(this.#timer);
  }

  #onSubmit = async (/** @type {SubmitEvent} */ event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    const button = form.querySelector('button');
    const status = form.querySelector('[data-km-status]');
    const label = form.querySelector('[data-km-label]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    const id = Number(new FormData(form).get('id'));
    if (!id) return;
    button?.setAttribute('aria-busy', 'true');
    try {
      // Tausch (snippets/km-teigleisten-addon.liquid): erst ein Paar heraus, dann das Set über den normalen Weg hinein,
      // dessen Antwort den Warenkorb mit beiden Änderungen neu zeichnet
      const swapKey = form.dataset.kmSwapKey;
      if (swapKey) {
        const response = await fetch(window.Theme?.routes?.cart_change_url ?? '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: swapKey, quantity: Math.max(Number(form.dataset.kmSwapQuantity) || 0, 0) }),
        });
        if (!response.ok) throw new Error('Swap failed');
      }
      await addItemsToCart(this, [{ id, quantity: 1 }]);
      this.setAttribute('state', 'added');
      if (label) label.textContent = 'Hinzugefügt';
      if (status) status.textContent = 'In den Warenkorb gelegt.';
    } catch (error) {
      this.setAttribute('state', 'error');
      if (status) status.textContent = 'Das hat leider nicht geklappt. Bitte versuch es noch einmal.';
    } finally {
      button?.removeAttribute('aria-busy');
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => {
        this.removeAttribute('state');
        if (label) label.textContent = 'In den Warenkorb';
        if (status) status.textContent = '';
      }, 2500);
    }
  };
}

/* ------------------------------------------------------------------ */
/* Seasonal date windows and countdowns                                */
/* ------------------------------------------------------------------ */

/** Local calendar date as YYYY-MM-DD (the shop and its customers are in Germany). */
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** @param {string} iso YYYY-MM-DD */
function daysUntil(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(y, (m || 1) - 1, d || 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/**
 * <km-season data-start="2026-10-01" data-end="2026-10-31" data-target="2026-10-31">
 * Shows itself only inside the (inclusive) date window and fills [data-km-days] elements from
 * data-many / data-one / data-today ("[tage]" = days left). Liquid renders the same state server-side;
 * this keeps cached pages correct and switches seasons by itself. With data-hide-section the whole
 * surrounding section is hidden outside the window (home page rows).
 */
class KmSeason extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.update();
    this.#timer = window.setInterval(() => this.update(), 15 * 60_000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  update() {
    const today = todayIso();
    const { start, end, target } = this.dataset;
    const active = (!start || start <= today) && (!end || today <= end);
    this.hidden = !active;
    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement && this.hasAttribute('data-hide-section')) section.hidden = !active;
    if (!active || !target) return;
    const days = daysUntil(target);
    for (const el of this.querySelectorAll('[data-km-days]')) {
      if (!(el instanceof HTMLElement)) continue;
      const text = days <= 0 ? el.dataset.today : days === 1 ? el.dataset.one : el.dataset.many;
      if (text) el.textContent = text.replace('[tage]', String(days));
    }
  }
}

/* ------------------------------------------------------------------ */
/* Horizontal product rows: previous/next buttons                      */
/* ------------------------------------------------------------------ */
class KmRow extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    const track = this.querySelector('[data-km-row-track]');
    if (!(track instanceof HTMLElement)) return;
    const { signal } = this.#abort;
    const step = (/** @type {number} */ dir) => {
      const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      track.scrollBy({ left: dir * rtl * track.clientWidth * 0.9, behavior: 'smooth' });
    };
    this.querySelector('[data-km-row-prev]')?.addEventListener('click', () => step(-1), { signal });
    this.querySelector('[data-km-row-next]')?.addEventListener('click', () => step(1), { signal });
    const sync = () => {
      const max = track.scrollWidth - track.clientWidth;
      const pos = Math.abs(track.scrollLeft);
      this.toggleAttribute('at-start', pos <= 2);
      this.toggleAttribute('at-end', pos >= max - 2);
      this.toggleAttribute('scrollable', max > 2);
    };
    track.addEventListener('scroll', sync, { passive: true, signal });
    window.addEventListener('resize', sync, { signal });
    sync();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }
}

/* ------------------------------------------------------------------ */
/* "Zuletzt angesehen" (localStorage, rendered by the search endpoint) */
/* ------------------------------------------------------------------ */
const VIEWED_KEY = 'km-viewed';

/** @returns {string[]} */
function readViewed() {
  try {
    const value = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
    return Array.isArray(value) ? value.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * Stores the current product id (max. 12, newest first) and, when the row comes near the viewport,
 * renders the other recently viewed products through /search?q=id:…&section_id=km-recently-viewed.
 */
class KmRecentlyViewed extends HTMLElement {
  #observer = /** @type {IntersectionObserver | null} */ (null);
  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    const current = this.dataset.productId || '';
    const limit = Number(this.dataset.limit || 4);
    const viewed = readViewed();
    const others = viewed.filter((id) => id !== current).slice(0, limit);
    if (current) {
      // Only with consent "Präferenzen" (assets/km-consent.js, 23.10.2026)
      const remember = () =>
        /** @type {any} */ (window).kmConsent?.allowed('preferences').then((/** @type {boolean} */ ok) => {
          if (!ok) return;
          try {
            localStorage.setItem(VIEWED_KEY, JSON.stringify([current, ...readViewed().filter((id) => id !== current)].slice(0, 12)));
          } catch {
            /* storage full or blocked: the row simply stays hidden */
          }
        });
      if (/** @type {any} */ (window).kmConsent) remember();
      else document.addEventListener('km:consent-api', remember, { once: true, signal: this.#abort.signal });
    }
    if (!others.length) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#load(others);
      },
      { rootMargin: '800px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abort.abort();
  }

  /** @param {string[]} ids */
  async #load(ids) {
    const url = new URL(window.Theme?.routes?.search_url ?? '/search', window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', this.dataset.sectionFile || 'km-recently-viewed');
    try {
      const html = await fetch(url, { signal: this.#abort.signal }).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = [...doc.querySelectorAll('[data-km-recent-item]')];
      const list = this.querySelector('[data-km-recent-list]');
      if (!items.length || !list) return;
      items.sort((a, b) => ids.indexOf(a.getAttribute('data-product-id') || '') - ids.indexOf(b.getAttribute('data-product-id') || ''));
      list.replaceChildren(...items.map((item) => document.importNode(item, true)));
      this.setAttribute('loaded', '');
    } catch {
      /* offline or aborted: keep the row hidden */
    }
  }
}

/* ------------------------------------------------------------------ */
/* Newsletter popup (sections/km-newsletter-popup.liquid)              */
/* ------------------------------------------------------------------ */
const NEWSLETTER_KEY = 'km-nl-hidden-until';

/**
 * Opens after data-delay seconds or on exit intent (desktop with mouse), at most once per page and not
 * while another dialog (cart drawer, search) is open. Desktop: modal dialog; phones: non-modal bottom sheet.
 * Closing or subscribing hides it for data-suppress-days (localStorage; blocked storage = no suppression).
 */
class KmNewsletterPopup extends HTMLElement {
  #abort = new AbortController();
  #timer = 0;
  #opened = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const dialog = this.dialog;
    if (!dialog) return;
    const { signal } = this.#abort;
    this.querySelector('[data-km-nl-close]')?.addEventListener('click', () => this.close(), { signal });
    this.querySelector('[data-km-nl-copy]')?.addEventListener('click', () => this.copy(), { signal });
    dialog.addEventListener('cancel', () => this.#suppress(), { signal });
    dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && !dialog.matches(':modal')) this.close();
      },
      { signal }
    );
    this.closest('form')?.addEventListener('submit', () => this.#suppress(), { signal });

    const state = this.dataset.state;
    const ownPost = window.location.hash === `#${this.dataset.formId}`;
    if (state === 'success' || (state === 'error' && ownPost)) {
      this.#suppress();
      this.open(true);
      return;
    }
    if (this.#suppressed()) return;

    this.#timer = window.setTimeout(() => this.open(), Number(this.dataset.delay || 20) * 1000);
    if (window.matchMedia('(min-width: 750px) and (pointer: fine)').matches) {
      document.addEventListener(
        'mouseout',
        (event) => {
          if (!event.relatedTarget && event.clientY <= 0) this.open();
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
    window.clearTimeout(this.#timer);
  }

  get dialog() {
    const dialog = this.querySelector('dialog');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /** @param {boolean} [force] open even if suppressed (success/error after submitting) */
  open(force = false) {
    const dialog = this.dialog;
    if (!dialog || dialog.open || (this.#opened && !force)) return;
    if (!force && this.#suppressed()) return;
    // Wait while another dialog (cart drawer, search) or Shopify's cookie banner is on screen.
    // (offsetParent is null for fixed elements, so visibility is checked via client rects.)
    const shown = (/** @type {Element} */ el) =>
      el instanceof HTMLElement && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
    const busy = [...document.querySelectorAll('dialog[open], #km-consent:not([hidden]), #shopify-pc__banner, #shopify-pc__prefs, #shopify-pc__prefs__dialog')].some(
      (el) => el !== this.dialog && shown(el)
    );
    if (!force && busy) {
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => this.open(), 10_000);
      return;
    }
    this.#opened = true;
    window.clearTimeout(this.#timer);
    if (window.matchMedia('(min-width: 750px)').matches) {
      dialog.showModal();
      const email = dialog.querySelector('input[type="email"]');
      if (email instanceof HTMLInputElement) email.focus();
    } else {
      dialog.show();
    }
  }

  close() {
    this.#suppress();
    this.dialog?.close();
  }

  async copy() {
    const code = this.querySelector('[data-km-nl-code]')?.textContent?.trim() || '';
    const button = this.querySelector('[data-km-nl-copy]');
    const status = this.querySelector('[data-km-nl-status]');
    try {
      await navigator.clipboard.writeText(code);
      if (button instanceof HTMLElement) button.textContent = button.dataset.done || 'Kopiert';
      if (status) status.textContent = `Code ${code} kopiert.`;
    } catch {
      const el = this.querySelector('[data-km-nl-code]');
      if (el) window.getSelection()?.selectAllChildren(el);
    }
  }

  #suppress() {
    try {
      const days = Number(this.dataset.suppressDays || 30);
      localStorage.setItem(NEWSLETTER_KEY, String(Date.now() + days * 86_400_000));
    } catch {
      /* storage blocked */
    }
  }

  #suppressed() {
    try {
      return Number(localStorage.getItem(NEWSLETTER_KEY) || 0) > Date.now();
    } catch {
      return false;
    }
  }
}

/* ------------------------------------------------------------------ */
/* B2B tier calculator                                                 */
/* ------------------------------------------------------------------ */
class KmB2bCalculator extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener('input', () => this.update(), { signal: this.#abort.signal });
    this.update();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @returns {{ min: number, price: number }[]} */
  get tiers() {
    try {
      return JSON.parse(this.dataset.tiers || '[]').sort((/** @type {any} */ a, /** @type {any} */ b) => a.min - b.min);
    } catch {
      return [];
    }
  }

  update() {
    const quantityInput = this.querySelector('[data-km-qty]');
    const packInput = this.querySelector('[data-km-pack]');
    if (!(quantityInput instanceof HTMLInputElement)) return;
    const tiers = this.tiers;
    const minimum = tiers[0]?.min ?? 50;
    const quantity = Math.max(Number(quantityInput.value) || 0, 0);
    const tier = [...tiers].reverse().find((t) => quantity >= t.min);
    // Verpackung: Auswahl (lose / Papiertüte mit Sticker / mit eigenem Logo-Sticker), Aufpreis je Stück in data-extra
    const packOption = packInput instanceof HTMLSelectElement ? packInput.selectedOptions[0] : null;
    const packing = packOption ? Number(packOption.dataset.extra || 0) : 0;
    const packLabel = packOption && packing ? packOption.dataset.label || '' : '';
    const set = (/** @type {string} */ key, /** @type {string} */ value) => {
      const el = this.querySelector(`[data-km-${key}]`);
      if (el) el.textContent = value;
    };

    for (const row of this.querySelectorAll('[data-km-tier]')) {
      row.toggleAttribute('active', Boolean(tier) && Number(/** @type {HTMLElement} */ (row).dataset.kmTier) === tier?.min);
    }

    if (!tier) {
      set('unit', '–');
      set('sum', '–');
      set('hint', `Staffelpreise gelten ab ${minimum} Stück. Kleinere Mengen bestellst du über „Selbst gestalten“.`);
    } else {
      const unit = tier.price + packing;
      set('unit', formatMoney(Math.round(unit * 100)));
      set('sum', formatMoney(Math.round(unit * quantity * 100)));
      const next = tiers.find((t) => t.min > quantity);
      set('hint', next ? `Ab ${next.min} Stück kostet einer ${formatMoney(Math.round((next.price + packing) * 100))}.` : 'Das ist die günstigste Staffel.');
    }

    // Mirror into the contact form so the request carries the calculation.
    const form = this.querySelector('form');
    const summary = form?.querySelector('[data-km-summary]');
    if (summary instanceof HTMLInputElement) {
      summary.value = tier
        ? `${quantity} Stück · ${formatMoney(Math.round((tier.price + packing) * 100))}/Stück${packLabel ? ` · ${packLabel}` : ''}`
        : `${quantity} Stück (unter Mindestmenge)`;
    }
  }
}

/* ------------------------------------------------------------------ */
/* Teigleisten as an add-on on product pages                           */
/* ------------------------------------------------------------------ */
/**
 * snippets/km-teigleisten-addon.liquid: radios „Ohne“ / Paar (with thickness) / 3er-Set. When the product form of the
 * same section is submitted with an add-on chosen, the add-on goes into the cart first (plain fetch, no event), then
 * the form is submitted again and Horizon adds the product and refreshes the drawer, which then shows both lines.
 * Listens in the capture phase on window, so it runs before Horizon's own submit handler.
 */
class KmAddon extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    // Choosing a thickness means choosing the pair
    this.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof HTMLSelectElement && event.target.matches('[data-km-addon-paar]')) {
          const radio = this.querySelector('[data-km-addon-paar-radio]');
          if (radio instanceof HTMLInputElement) radio.checked = true;
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** Variant ID of the chosen add-on, 0 for „Ohne“. */
  get variantId() {
    const checked = this.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement) || !checked.value) return 0;
    if (checked.value === 'paar') {
      const select = this.querySelector('[data-km-addon-paar]');
      return select instanceof HTMLSelectElement ? Number(select.value) : 0;
    }
    return Number(checked.value);
  }

  #onSubmit = async (/** @type {SubmitEvent} */ event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component')) return;
    const scope = this.closest('.shopify-section');
    if (scope && !scope.contains(form)) return;
    if (form.dataset.kmAddonDone === '1') {
      delete form.dataset.kmAddonDone;
      return;
    }
    const id = this.variantId;
    if (!id) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const status = this.querySelector('[data-km-addon-status]');
    try {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');
      if (status) status.textContent = 'Die Teigleisten liegen mit im Warenkorb.';
      const none = this.querySelector('input[type="radio"][value=""]');
      if (none instanceof HTMLInputElement) none.checked = true;
    } catch {
      if (status) status.textContent = 'Die Teigleisten konnten wir nicht dazulegen. Bitte wähl sie noch einmal.';
    }
    // The product itself goes in either way
    form.dataset.kmAddonDone = '1';
    const submitter = event.submitter instanceof HTMLButtonElement && event.submitter.form === form ? event.submitter : undefined;
    form.requestSubmit(submitter);
  };
}

/* ------------------------------------------------------------------ */
/* Registration                                                        */
/* ------------------------------------------------------------------ */
const defineOnce = (/** @type {string} */ name, /** @type {CustomElementConstructor} */ ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

defineOnce('km-free-shipping', KmFreeShipping);
defineOnce('km-delivery', KmDelivery);
defineOnce('km-bundle', KmBundle);
defineOnce('km-set-builder', KmSetBuilder);
defineOnce('km-card-add', KmCardAdd);
defineOnce('km-season', KmSeason);
defineOnce('km-row', KmRow);
defineOnce('km-recently-viewed', KmRecentlyViewed);
defineOnce('km-newsletter-popup', KmNewsletterPopup);
defineOnce('km-b2b-calculator', KmB2bCalculator);
defineOnce('km-addon', KmAddon);
